// Trainee routes: reader screens. Import ONLY the trainee token subset (R-VIS-04); never the interpretation chip.
import { useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { emerald } from "@coachbrain/design-tokens/trainee";
import { Card, PrimaryButton, ProgressBar, SectionLabel, TapAnswers, Num, Measured, Columns, Col } from "@coachbrain/ui";
import { ErrorBox, Loading, TraineeShell } from "../../components/common";
import { useLoad } from "../../lib/hooks";
import { useSession } from "../../lib/session";
import { dateDayMonth } from "../../lib/format";
import { ReportBody } from "../reports";
import { en } from "../../i18n/en";

const t = en.trainee;

/** The coach's display name ("Coach Dani") comes from the API; the first name is what the copy uses. */
const firstName = (n: string) => n.replace(/^Coach\s+/i, "").split(" ")[0] ?? n;

export function TraineeHomeRoute() {
  const navigate = useNavigate();
  const { api, data, error, reload, setData } = useLoad((a) => a.traineeHome());
  if (error) return <TraineeShell><ErrorBox message={error} onRetry={reload} /></TraineeShell>;
  if (!data || !api) return <TraineeShell><Loading /></TraineeShell>;
  const h = data;
  const coachFirst = h.coachName.replace(/^Coach /, "");
  return (
    <TraineeShell title={t.hi(h.greeting.replace(/^Hi /, ""))} subtitle={h.nextSession ? t.nextSession(h.nextSession) : undefined} right={h.coachName}>
      <Columns kind="two">
      <Col area="left">
      <Card tone="goal">
        <SectionLabel tone="emerald">{t.yourGoal}</SectionLabel>
        <div className="cb-h2">“{h.goal.verbatim}”</div>
        <ProgressBar fraction={h.goal.fraction} label={h.goal.progressText} />
        <div className="cb-row-main cb-small cb-strong cb-text-emerald"><span>{h.goal.progressText}</span><span>{h.goal.targetText}</span></div>
      </Card>
      <div className="cb-grid-2">
        {h.tiles.map((tile) => <Card key={tile.label} className="cb-tile"><Num className="cb-tile-value">{tile.value}</Num><SectionLabel>{tile.label}</SectionLabel><span className={`cb-small cb-strong ${/faster|slower/.test(tile.sub) ? "cb-measured" : "cb-muted"}`}>{tile.sub}</span></Card>)}
      </div>
      {h.milestone ? <Card tone="mint" style={{ flexDirection: "row", alignItems: "center", gap: 16 }}><Num className="cb-tile-value" style={{ color: emerald, fontSize: 40 }}>{h.milestone.value}</Num><div><SectionLabel tone="emerald">{t.milestoneReached}</SectionLabel><div className="cb-strong">{h.milestone.text}</div></div></Card> : null}
      </Col>
      <Col area="right">
      {h.question ? (
        <Card tone="measured">
          <SectionLabel tone="blue">{t.asked(coachFirst)}</SectionLabel>
          <div className="cb-h2">{h.question.text}</div>
          <TapAnswers options={h.question.options?.filter((o) => o !== "Ask again").map((o) => (o === "No pain" ? "None" : o === "Some" ? "A little" : o)) ?? ["None", "A little"]} onPick={async (o) => { await api.traineeAnswer(h.question!.id, o); setData(await api.traineeHome()); }} />
        </Card>
      ) : null}
      {h.report ? (
        <Card>
          <div className="cb-row-main"><span className="cb-h3">{t.reportReady(h.report.period.label)}</span><button type="button" className="cb-link-button" onClick={() => void navigate({ to: `/t/reports/${h.report!.id}` })}>{t.read}</button></div>
          <div className="cb-body">“{h.report.coachNote.split(" — ")[0]}{h.report.coachNote.includes(" — ") ? "." : ""}”</div>
          <span className="cb-small cb-strong cb-muted">{t.from(h.coachName, dateDayMonth(h.report.createdAt))}</span>
        </Card>
      ) : null}
      {h.sharedSessions.length ? <Card onClick={() => void navigate({ to: `/t/sessions/${h.sharedSessions[0]!.id}` })}><SectionLabel>{t.sharedWithYou}</SectionLabel><div className="cb-h3">{h.sharedSessions[0]!.title}</div></Card> : null}
      </Col>
      </Columns>
    </TraineeShell>
  );
}

export function TraineeReportsRoute() {
  const navigate = useNavigate();
  const { data } = useLoad((a) => a.traineeHome());
  return <TraineeShell title={en.nav.reports}>{!data ? <Loading /> : data.report ? <Card onClick={() => void navigate({ to: `/t/reports/${data.report!.id}` })}><span className="cb-h3">{data.report.period.label}</span><span className="cb-small cb-muted">{t.from(data.coachName, dateDayMonth(data.report.createdAt))}</span></Card> : <Card><span className="cb-muted">No reports yet.</span></Card>}</TraineeShell>;
}

export function TraineeReportRoute() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const me = useSession();
  const { data, error, reload } = useLoad((a) => Promise.all([a.traineeReport(id), a.traineeHome()]), [id]);
  if (error) return <TraineeShell><ErrorBox message={error} onRetry={reload} /></TraineeShell>;
  if (!data) return <TraineeShell><Loading /></TraineeShell>;
  const [r, home] = data;
  const traineeName = me?.user.name ?? home.greeting.replace(/^Hi /, "");
  return (
    <TraineeShell chrome="emerald" title={<span>{en.reports.progress}<br />{firstName(traineeName)} · {r.period.label}</span>} subtitle={r.approvedAt ? en.reports.approvedSent(home.coachName, dateDayMonth(r.approvedAt)) : undefined} right="">
      <Columns kind="main-aside">
      <Col area="main" className="cb-report-preview"><ReportBody r={r} coachName={home.coachName} clientName={traineeName} /></Col>
      <Col area="aside"><div className="cb-btn-row" style={{ flexDirection: "column" }}><PrimaryButton tone="navy" onClick={() => window.print()}>{en.reports.savePdf}</PrimaryButton><PrimaryButton tone="outline" onClick={() => void navigate({ to: "/t/coach" })}>{en.reports.reply(firstName(home.coachName))}</PrimaryButton></div></Col>
      </Columns>
    </TraineeShell>
  );
}

export function TraineeSessionsRoute() {
  const navigate = useNavigate();
  const { data } = useLoad((a) => a.traineeHome());
  return <TraineeShell title={en.nav.sessions} subtitle={t.sharedWithYou}><Columns kind="cards">{!data ? <Loading /> : data.sharedSessions.map((s) => <Card key={s.id} onClick={() => void navigate({ to: `/t/sessions/${s.id}` })}><span className="cb-h3">{s.title}</span><span className="cb-small cb-muted">{s.coachNote}</span></Card>)}</Columns></TraineeShell>;
}

export function TraineeSessionRoute() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const { api, data, error, reload } = useLoad((a) => Promise.all([a.traineeSession(id), a.traineeHome()]), [id]);
  const [started, setStarted] = useState(false);
  const [done, setDone] = useState<Set<string>>(new Set());
  const [hurt, setHurt] = useState<boolean | null>(null);
  if (error) return <TraineeShell><ErrorBox message={error} onRetry={reload} /></TraineeShell>;
  if (!data || !api) return <TraineeShell><Loading /></TraineeShell>;
  const [s, home] = data;
  const coach = firstName(home.coachName);
  const entries = s.blocks.flatMap((b) => b.entries);
  return (
    <TraineeShell title={s.title} subtitle={t.forTravelling(coach)} right={t.sharedWithYou}>
      <Columns kind="two">
      <Col area="left">
      {s.coachNote ? <Card tone="measured"><SectionLabel tone="blue">{t.note(coach)}</SectionLabel><div className="cb-body">{s.coachNote}</div></Card> : null}
      <Card className="cb-card-flush">
        {entries.map((e) => <div key={e.id} className="cb-row"><div className="cb-row-main"><span className="cb-row-left">{e.exerciseText}</span><Num className="cb-row-right">{e.sets[0]?.timeSeconds != null ? `${Math.round(e.sets[0].timeSeconds / 60)} min` : `${e.sets.length} × ${e.sets[0]?.reps ?? ""}${e.exerciseText.toLowerCase().includes("split") ? " each" : ""}`}</Num></div>{e.notes ? <div className="cb-row-sub">{e.notes}</div> : null}</div>)}
      </Card>
      </Col>
      <Col area="right">
      {!started ? <PrimaryButton tone="emerald" full onClick={() => setStarted(true)}>{t.start}</PrimaryButton> : (
        <>
          <Card className="cb-card-flush">
            <div><SectionLabel>{t.logAsYouGo}</SectionLabel></div>
            {entries.flatMap((e) => e.sets.map((set, i) => { const k = `${e.id}:${set.id}`; const isDone = done.has(k); return <div key={k} className="cb-row-main"><span className="cb-strong" style={{ fontSize: 15 }}>{e.exerciseText} · set {i + 1}</span>{isDone ? <span className="cb-badge cb-badge-approved">{t.done}</span> : <button type="button" className="cb-chip cb-chip-ground" onClick={() => setDone((d) => new Set(d).add(k))}>{t.markDone}</button>}</div>; }))}
            <div className="cb-row-main"><span className="cb-strong" style={{ fontSize: 15 }}>{t.hurt}</span><span className="cb-inline-actions"><button type="button" className={`cb-chip cb-chip-ground ${hurt === false ? "cb-chip-selected" : ""}`} onClick={() => setHurt(false)}>No</button><button type="button" className={`cb-chip cb-chip-ground ${hurt === true ? "cb-chip-selected" : ""}`} onClick={() => setHurt(true)}>Yes</button></span></div>
          </Card>
          <Card style={{ background: "var(--cb-ground)" }}><span className="cb-small cb-muted">{t.supervisedNote(coach)}</span></Card>
          <PrimaryButton tone="navy" full onClick={async () => { await api.traineeLog({ sessionTemplateId: s.id, done: [...done].map((k) => { const [entryId, setId] = k.split(":"); return { entryId: entryId!, setId: setId! }; }), hurt }); void navigate({ to: "/t/home" }); }}>{t.finish}</PrimaryButton>
        </>
      )}
      </Col>
      </Columns>
    </TraineeShell>
  );
}

export function TraineeCoachRoute() {
  const { api, data, reload } = useLoad((a) => a.traineeData());
  const { data: home } = useLoad((a) => a.traineeHome());
  const coachName = home?.coachName ?? en.nav.coach;
  const [msg, setMsg] = useState(""); const [sent, setSent] = useState(false);
  return (
    <TraineeShell title={coachName} subtitle="Message, and your data">
      <Columns kind="two">
      <Col area="left">
      <Card>
        <div className="cb-field"><label htmlFor="m">{t.message}</label><textarea id="m" className="cb-textarea" style={{ minHeight: 80 }} value={msg} onChange={(e) => setMsg(e.target.value)} maxLength={500} /></div>
        <PrimaryButton tone="navy" full disabled={!msg.trim() || !api} onClick={async () => { await api!.traineeMessage(msg); setMsg(""); setSent(true); }}>Send</PrimaryButton>
        {sent ? <span className="cb-small cb-text-emerald">{t.messageSent(firstName(coachName))}</span> : null}
      </Card>
      </Col>
      <Col area="right">
      <Card className="cb-card-flush">
        <div><SectionLabel>{t.yourData}</SectionLabel></div>
        {!data ? <Loading /> : data.summary.map((row) => <div key={row.label} className="cb-row-main"><span>{row.label}</span><Measured>{row.value}</Measured></div>)}
        <div className="cb-inline-actions"><button type="button" className="cb-chip cb-chip-ground" onClick={() => void reload()}>{t.exportData}</button><button type="button" className="cb-chip cb-chip-ground" onClick={async () => { await api?.traineeRevokeConsent("chn_fred_sms"); }}>{t.revoke}</button><button type="button" className="cb-chip cb-chip-unresolved" onClick={async () => { if (confirm("Delete your account? Your coach keeps the practice record.")) await api?.traineeDelete(); }}>{t.deleteAccount}</button></div>
      </Card>
      </Col>
      </Columns>
    </TraineeShell>
  );
}
